import { LoginResponse } from '@/types/auth';

const TOKEN_KEY = 'pickem_auth_token';
const REFRESH_TOKEN_KEY = 'pickem_refresh_token';
const USER_KEY = 'pickem_user';
const SSO_TOKEN_KEY = 'pickem_sso_token';

/**
 * Extract token from URL query string
 * In dev mode, returns '12345' if no token is found
 */
export const extractTokenFromUrl = (): string | null => {
  console.log('[Auth] extractTokenFromUrl - START', {
    url: window.location.href,
    search: window.location.search,
  });

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  if (token) {
    console.log('[Auth] extractTokenFromUrl - Token found in URL', {
      tokenPreview: `${token.substring(0, 30)}...`,
      tokenLength: token.length,
    });

    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;

    // Only clean up URL if not in iframe (to allow multiple components to read the token)
    if (!isInIframe) {
      console.log('[Auth] extractTokenFromUrl - Cleaning URL (not in iframe)');
      // Clean up URL by removing the token parameter
      params.delete('token');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } else {
      console.log('[Auth] extractTokenFromUrl - Keeping URL as-is (in iframe)');
    }
    return token;
  }

  // If COERCE_USER_TOKEN is enabled, return hardcoded token for testing
  if (import.meta.env.VITE_COERCE_USER_TOKEN === '1') {
    console.log('[Auth] COERCE_USER_TOKEN enabled: Using hardcoded token "12345"');
    return '12345';
  }

  console.log('[Auth] extractTokenFromUrl - No token found in URL');
  return null;
};

/**
 * Validate JWT token format (basic validation)
 */
export const isValidTokenFormat = (token: string): boolean => {
  if (!token) return false;
  const parts = token.split('.');
  return parts.length === 3;
};

/**
 * Decode JWT token payload (without verification)
 */
export const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  return Date.now() >= expirationTime;
};

/**
 * Store authentication data
 */
export const storeAuthData = (data: LoginResponse): void => {
  console.log('[Auth] storeAuthData - Storing auth data', {
    hasToken: !!data.token,
    hasRefreshToken: !!data.refreshToken,
    hasUser: !!data.user,
    userId: data.user?.id,
  });

  if (data.token) {
    console.log('[Auth] storeAuthData - Storing SSO token', {
      tokenPreview: `${data.token.substring(0, 30)}...`,
      storageKey: SSO_TOKEN_KEY,
    });
    sessionStorage.setItem(SSO_TOKEN_KEY, data.token);
  }
  if (data.refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  }
  if (data.user) {
    sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
  }

  console.log('[Auth] storeAuthData - Auth data stored successfully');
};

/**
 * Get stored token
 */
export const getStoredToken = (): string | null => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  console.log('[Auth] getStoredToken - Retrieving legacy token', {
    found: !!token,
    tokenPreview: token ? `${token.substring(0, 30)}...` : null,
    storageKey: TOKEN_KEY,
  });
  return token;
};

/**
 * Get stored refresh token
 */
export const getStoredRefreshToken = (): string | null => {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Get stored user
 */
export const getStoredUser = (): any | null => {
  const userStr = sessionStorage.getItem(USER_KEY);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Set stored user
 */
export const setStoredUser = (user: any): void => {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
};

/**
 * Get SSO token directly from iframe URL query params
 * This is the ONLY source of truth - never cache tokens
 */
export const getTokenFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  console.log('[Auth] getTokenFromUrl - Reading token from URL (source of truth)', {
    found: !!token,
    tokenPreview: token ? `${token.substring(0, 30)}...` : null,
    url: window.location.href,
  });

  return token;
};

/**
 * Store SSO token and user ID
 * @deprecated Token should only be read from URL, never stored
 */
export const storeSsoToken = (_token: string, _ssoUserId: string): void => {
  console.log('[Auth] storeSsoToken - DEPRECATED: Should not store tokens, use getTokenFromUrl()');
  // No-op: tokens should always be read from URL
};

/**
 * Get stored SSO token from sessionStorage
 * This retrieves the JWT token returned from /api/login after play-box authentication
 */
export const getStoredSsoToken = (): string | null => {
  const token = sessionStorage.getItem(SSO_TOKEN_KEY);
  console.log('[Auth] getStoredSsoToken - Retrieved from sessionStorage', {
    found: !!token,
    tokenPreview: token ? `${token.substring(0, 30)}...` : null,
    storageKey: SSO_TOKEN_KEY,
  });
  return token;
};

/**
 * Get stored SSO user ID
 * @deprecated User ID should be derived from URL token
 */
export const getStoredSsoUserId = (): string | null => {
  console.log('[Auth] getStoredSsoUserId - DEPRECATED');
  return null;
};

/**
 * Clear all authentication data including SSO tokens
 */
export const clearAuthData = (): void => {
  console.log('[Auth] clearAuthData - Clearing all auth tokens from storage', {
    keys: [TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY, SSO_TOKEN_KEY],
  });

  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(SSO_TOKEN_KEY);

  console.log('[Auth] clearAuthData - All auth data cleared');
};

/**
 * Auto-login check on app mount
 */
export const checkAutoLogin = (): { token: string | null; needsRefresh: boolean } => {
  // First check URL for token (also handles dev mode fallback)
  const urlToken = extractTokenFromUrl();
  if (urlToken) {
    // In dev mode, the token might be '12345' which is not a valid JWT format
    // but we should still use it for authentication
    if (import.meta.env.DEV || isValidTokenFormat(urlToken)) {
      return { token: urlToken, needsRefresh: false };
    }
  }

  // Then check stored token
  const storedToken = getStoredToken();
  if (storedToken && isValidTokenFormat(storedToken)) {
    const expired = isTokenExpired(storedToken);
    return { token: expired ? null : storedToken, needsRefresh: expired };
  }

  return { token: null, needsRefresh: false };
};
